import CloseButton from 'engage/components/CloseButton/CloseButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './StorySchedule.module.scss';
import cx from 'classnames';
import { format } from 'date-fns';
import { millisecondsInHour } from 'date-fns/constants';
import NotificationRangeSelection from './NotificationRangeSelection';
import { v4 as uuidv4 } from 'uuid';
import { CustomButton } from 'engage/components/Button/Button';
import { DEADLINE_TYPE, SCHEDULE_TYPE } from '../../constants';
import { getMsTime, getTodaysMinTime } from '../../utils';

const MAX_YEARS_AHEAD = 100;

const initScheduleNotification = {
    tempId: uuidv4(), offset: millisecondsInHour, sendEmail: false,
}

const StorySchedule = ({ storySchedule = null, onSubmit, onClose, onRemove, minimize = false, onOptionChanged = null, editing = false, defaultNotification = false, allowStartDateExpired = false }) => {
    const currentTimestamp = useMemo(() => {
        // Get current time till the minute (ignoring seconds & milliseconds)
        const time = Date.now()
        return time - time % 60000;
    });
    const { t } = useTranslation();

    const [start, setStart] = useState(getMsTime(storySchedule?.start) ?? (getTodaysMinTime().getTime()));
    const [end, setEnd] = useState(getMsTime(storySchedule?.end) ?? null);
    const [notes, setNotes] = useState(storySchedule?.notes ?? '');
    const [notifications, setNotifications] = useState(storySchedule?.notifications?.length ? storySchedule?.notifications : defaultNotification ? [initScheduleNotification] : []);
    const [showDeadline, setShowDeadline] = useState(storySchedule?.end ? true : false);
    const [isDateEdited, setIsDateEdited] = useState({ start: false, end: false });
    const [loading, setLoading] = useState(false);

    const showDelete = useMemo(() => !storySchedule?.new && storySchedule?.start, [storySchedule]);

    const startMinTime = useMemo(() => {
        const date = getTodaysMinTime();
        return date.getTime()
    }, []);

    const maxTime = useMemo(() => {
        const date = new Date(currentTimestamp);
        const year = date.getFullYear();
        return format(currentTimestamp, `yyyy-MM-dd'T'HH:mm`).replace(`${year}`, `${year + MAX_YEARS_AHEAD}`)
    }, []);

    const endMinTime = useMemo(() => {
        if (start) {
            return format(start + millisecondsInHour * 24 * 2, `yyyy-MM-dd'T'HH:mm`)
        }
    }, [start]);

    const type = useMemo(() => end ? DEADLINE_TYPE : SCHEDULE_TYPE, [end]);

    const isStartDataValid = useMemo(() => {
        if (!isDateEdited.start && allowStartDateExpired && getMsTime(storySchedule?.start) == start) {
            return true;
        }
        return start >= startMinTime;
    }, [start, storySchedule, allowStartDateExpired, startMinTime])

    const isValidData = useMemo(() => {
        if (!isStartDataValid) {
            return false;
        }

        if (type == SCHEDULE_TYPE) {
            if (notifications?.length && notifications?.some(item => item?.tempId ? !item?.offset || item?.offset < 0 || start - item.offset < currentTimestamp : false)) {
                return false;
            }
        }
        if (type == DEADLINE_TYPE) {
            if (!end || end < start) {
                return false;
            }
            if (notifications?.some(item => item?.tempId ? !item.offset || start - item.offset < currentTimestamp : false)) {
                return false;
            }
        }

        return true;
    }, [start, end, type, notes, notifications, startMinTime, isStartDataValid]);

    const isDataChanged = useMemo(() => {
        if (storySchedule?.edited) {
            return true;
        }
        if (isDateEdited.start || (storySchedule?.start ? getMsTime(storySchedule?.start) !== start : start != null)) {
            return true;
        }
        if (isDateEdited.end || (storySchedule?.end ? getMsTime(storySchedule?.end) !== end : end != null)) {
            return true;
        }
        if (storySchedule?.notes !== notes) {
            return true;
        }
        if (storySchedule?.notifications?.length !== notifications?.length) {
            return true;
        }
        if (storySchedule?.notifications?.length && storySchedule?.notifications?.some((item, index) => item.offset !== notifications[index]?.offset)) {
            return true;
        }
        return false;
    }, [start, end, notes, notifications, isDateEdited, storySchedule]);

    const modalRef = useRef(null);

    const handleStartDateChangeEvent = useCallback((e) => {
        setIsDateEdited(prevState => ({ ...prevState, start: true }));
        if (e.target.value && isNaN(e.target.value)) {
            const newStartTime = new Date(e.target.value).getTime();
            setStart(newStartTime);
            onOptionChanged && onOptionChanged({ start: newStartTime });
            if (type == DEADLINE_TYPE && end < newStartTime) {
                setEnd(newStartTime + millisecondsInHour * 24 * 2);
                onOptionChanged && onOptionChanged({ end: newStartTime + millisecondsInHour * 24 * 2 });
            }
        }
    }, [end, type, startMinTime]);

    const handleEndDateChangeEvent = (e) => {
        if (!isNaN(e.target.value)) {
            return;
        }
        setEnd(new Date(e.target.value).getTime());
        setIsDateEdited(prevState => ({ ...prevState, end: true }));
        onOptionChanged && onOptionChanged({ end: new Date(e.target.value).getTime() });
    }

    const handleNotesChanged = (e) => {
        setNotes(e.target.value);
    }

    const handleToggleDeadline = useCallback(() => {
        if (showDeadline) {
            setEnd(null);
        } else {
            setEnd(start + millisecondsInHour * 24 * 2);
        }
        setShowDeadline(prevState => !prevState);
    }, [showDeadline, start]);

    const handleNotificationChange = (id, option) => {
        setNotifications(prevState => prevState.map(item => {
            if (item.id === id) {
                return { ...item, ...option }
            }
            return item;
        }));
    }

    const handleRemoveNotification = (id) => {
        setNotifications(prevState => prevState.filter(item => item.id !== id && item.tempId !== id));
    }

    const handleAddNotification = useCallback(() => {
        setNotifications(prevState => ([...prevState, initScheduleNotification]));
    }, [type]);

    const handleSaveSchedule = useCallback(async () => {
        setLoading(true);
        await onSubmit(start, end, notes, notifications);
        setLoading(false);
    }, [start, end, notes, notifications]);

    const handleScheduleRemove = () => {
        const confirmRemove = window.confirm(t('storyboard.editor.schedule.confirm_remove'));
        if (confirmRemove) {
            onRemove();
        }
    }

    useEffect(() => {
        return () => {
            setStart(null);
            setEnd(null);
            setNotifications([]);
        }
    }, [])

    useEffect(() => {
        if (Object.keys(storySchedule).length) {
            const startTime = getMsTime(storySchedule?.start)
            if (startTime && startTime !== start) {
                setStart(startTime);
                setIsDateEdited(prevState => ({ ...prevState, start: false }));
            }
            const endTime = getMsTime(storySchedule?.end)
            if (endTime !== end) {
                console.log('end', endTime);
                setEnd(endTime);
                setShowDeadline(endTime !== null);
                setIsDateEdited(prevState => ({ ...prevState, end: false }));
            }
        } else {
            setNotifications(defaultNotification ? [initScheduleNotification] : []);
        }
    }, [storySchedule?.start, storySchedule?.end])


    return <div className={cx(styles.wrapper, { [styles.minimize]: minimize })} ref={modalRef}>
        <div className={styles.header}>
            <h2 className={styles.title}>{t('storyboard.editor.schedule.change')}</h2>
            <CloseButton onClose={onClose} />
        </div>
        <div className={styles.content}>
            <div className={styles.sectionContainer}>
                {!minimize && <h3 className={styles.sectionTitle}>
                    {t('my_video.tools.trim.start')}
                </h3>}
                <div className={cx(styles.inputs, { [styles.minimize]: minimize })}>
                    <div className={styles.timeContainer}>
                        <input
                            type="datetime-local"
                            name="startDate"
                            min={format(startMinTime, `yyyy-MM-dd'T'HH:mm`)}
                            max={maxTime}
                            className={cx(styles.dataInput, { [styles.invalid]: !isStartDataValid })}
                            value={format(start, `yyyy-MM-dd'T'HH:mm`)}
                            placeholder={t('company.settings.usage.start_date')}
                            onChange={handleStartDateChangeEvent}
                            disabled={!editing}
                        />
                        <span className={cx(styles.invalidMessage, { [styles.visible]: !isStartDataValid })}>{t('storyboard.editor.schedule.start_date_error')}</span>
                    </div>
                    {!minimize && editing && <CustomButton
                        variant='link'
                        className={styles.deadlineBtn}
                        label={`${showDeadline ? '-' : '+'} ${t(showDeadline ? 'storyboard.editor.schedule.remove_deadline' : 'storyboard.editor.schedule.add_deadline')}`}
                        onClick={handleToggleDeadline}
                    />}
                    <div className={cx(styles.timeContainer, styles.deadlineContainer, { [styles.visible]: showDeadline, [styles.includeInvalidMessage]: end && end < start })}>
                        <input
                            type="datetime-local"
                            name="startDate"
                            min={endMinTime}
                            className={cx(styles.dataInput, { [styles.invalid]: end < start })}
                            value={format(end ?? 0, `yyyy-MM-dd'T'HH:mm`)}
                            max={maxTime}
                            placeholder={t('company.settings.usage.start_date')}
                            onChange={handleEndDateChangeEvent}
                            disabled={!editing}
                        />
                        <span className={cx(styles.invalidMessage, { [styles.visible]: end < start })}>{t('storyboard.editor.schedule.deadline_date_error')}</span>
                    </div>
                </div>
            </div>
            <div className={styles.sectionContainer}>
                <h3 className={styles.sectionTitle}>
                    {t('storyboard.editor.schedule.notification')}
                </h3>
                <span className={styles.notificationsSubheader}>{t('storyboard.editor.schedule.notification_subheader')}</span>
                {notifications?.length ?
                    <NotificationRangeSelection
                        key={notifications[0]?.id ?? notifications[0]?.tempId}
                        startTime={start}
                        notification={notifications[0]}
                        onChange={handleNotificationChange}
                        onRemove={handleRemoveNotification}
                        disabled={loading || allowStartDateExpired && start < currentTimestamp} />
                    : null}
                {!notifications?.length && <CustomButton
                    onClick={handleAddNotification}
                    variant='link'
                    label={`+ ${t('storyboard.editor.schedule.notification.add')}`}
                    className={styles.addNotificationBtn}
                    disabled={allowStartDateExpired && start < currentTimestamp}
                />}
            </div>
            <div className={styles.sectionContainer}>
                <h3 className={styles.sectionTitle}>
                    {t('storyboard.editor.schedule.notes')}
                </h3>
                <textarea
                    className={styles.notes}
                    value={notes}
                    onChange={handleNotesChanged}
                    disabled={!editing}
                />
            </div>
        </div>
        <div className={styles.footer}>
            {showDelete && editing ? <CustomButton variant='danger-outline' className={styles.deleteBtn} label={t('video.settings.delete')} onClick={handleScheduleRemove} loading={loading} /> : null}
            <div className={styles.controlBtns}>
                {!loading && <CustomButton variant='secondary' label={t('general.cancel')} onClick={onClose} />}
                <CustomButton className={styles.saveBtn} variant='primary' label={t('general.save')} onClick={handleSaveSchedule} disabled={!isValidData || !isDataChanged} loading={loading} loadingSpinner={true} />
            </div>
        </div>
    </div>
}

export default StorySchedule;